import React, { useEffect, useState } from 'react';
import { BookOpen, CirclePlay, LifeBuoy, ListChecks, Search, Settings2 } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { C } from '@/design-system/dna';
import { HelpBody, useHelp } from './Help';
import { CampoEditor, EditButtons, FeaturePanel, MaterialUpload, MaterialView, resetHelp, SecaoEditor } from './HelpEditors';

function Secoes({ secoes, editavel, section }) {
  const { setData } = useHelp();
  const [edit, setEdit] = useState(null);
  return (
    <Accordion type="single" collapsible defaultValue={section || secoes[0]?.id} className="space-y-2">
      {secoes.map((s, n) => (
        <AccordionItem key={s.id} value={s.id} className="rounded-2xl border bg-white px-4" style={{ borderColor: C.line }} data-testid={`help-secao-${s.id}`}>
          <AccordionTrigger className="text-left text-[14px] font-extrabold hover:no-underline" style={{ color: C.ink }}><span className="flex items-center gap-2"><span className="flex h-6 w-6 items-center justify-center rounded-full text-[11px] text-white" style={{ background: C.purple }}>{n + 1}</span>{s.titulo}</span></AccordionTrigger>
          <AccordionContent>
            {edit === s.id ? <SecaoEditor s={s} onClose={() => setEdit(null)} /> : (
              <>
                <ol className="space-y-1.5 pl-1 text-[13px]" style={{ color: C.ink }}>{s.passos.map((p, i) => <li key={i} className="flex gap-2"><ListChecks className="mt-0.5 h-4 w-4 shrink-0" style={{ color: C.green }} />{p}</li>)}</ol>
                {editavel && <div className="mt-2 flex justify-end"><EditButtons testid={`help-secao-${s.id}`} editado={s.editado} onEdit={() => setEdit(s.id)} onReset={() => resetHelp('secao', s.id, setData)} /></div>}
              </>
            )}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

function Campos({ campos, editavel }) {
  const { setData } = useHelp();
  const [q, setQ] = useState('');
  const [edit, setEdit] = useState(null);
  const n = q.trim().toLowerCase();
  const list = Object.entries(campos).filter(([, h]) => !n || `${h.titulo} ${h.para_que} ${h.como}`.toLowerCase().includes(n));
  return (
    <div className="space-y-2">
      <div className="relative"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: C.muted }} /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar campo (ex.: evidência, reforço)" className="rounded-full pl-9" data-testid="help-campos-busca" /></div>
      {list.map(([k, h]) => (
        <div key={k} className="rounded-2xl border bg-white p-4" style={{ borderColor: C.line }} data-testid={`help-campo-${k}`}>
          <div className="mb-2 flex items-center justify-between gap-2"><b className="text-[13.5px]" style={{ color: C.ink }}>{h.titulo}</b>{editavel && edit !== k && <EditButtons testid={`help-campo-${k}`} editado={h.editado} onEdit={() => setEdit(k)} onReset={() => resetHelp('campo', k, setData)} />}</div>
          {edit === k ? <CampoEditor k={k} h={h} onClose={() => setEdit(null)} /> : <HelpBody h={h} />}
        </div>
      ))}
    </div>
  );
}

function Materiais({ materiais, editavel }) {
  return (
    <div className="space-y-3">
      {Object.entries(materiais).map(([k, m]) => (
        <div key={k} className="rounded-2xl border bg-white p-4" style={{ borderColor: C.line }} data-testid={`help-material-${k}`}>
          <p className="mb-2 flex items-center gap-2 text-[13.5px] font-extrabold" style={{ color: C.ink }}>{k === 'video' ? <CirclePlay className="h-4 w-4" style={{ color: C.purple }} /> : <BookOpen className="h-4 w-4" style={{ color: C.purple }} />}{m.titulo}</p>
          <MaterialView k={k} m={m} />
          {editavel && <MaterialUpload k={k} m={m} />}
        </div>
      ))}
    </div>
  );
}

/** Central de Ajuda do DNA Mapa: tutoriais, glossário de campos, materiais e (coordenação) edição dos textos. */
export function HelpCenter({ open, onClose, section, onRestartTour }) {
  const { data } = useHelp();
  const [rec, setRec] = useState(null);
  const [tab, setTab] = useState('tutoriais');
  useEffect(() => { if (open) { setTab(section === 'campos' ? 'campos' : 'tutoriais'); api.get('/dna-mapa/recurso').then(({ data: d }) => setRec(d)).catch(() => {}); } }, [open, section]);
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="overflow-y-auto bg-[#FBF9FD] p-0" style={{ width: 'min(640px, 100vw)', maxWidth: '100vw' }} data-testid="dna-help-center">
        <SheetHeader className="sticky top-0 z-10 border-b bg-white px-6 py-4 text-left" style={{ borderColor: C.line }}>
          <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: C.purple }}><LifeBuoy className="h-3.5 w-3.5" /> Central de Ajuda</p>
          <SheetTitle className="text-xl" style={{ color: C.ink }}>Como usar o DNA Mapa</SheetTitle>
          <SheetDescription>Orientações práticas para registrar com segurança e qualidade.</SheetDescription>
          <Button type="button" variant="outline" size="sm" className="mt-2 w-fit" onClick={onRestartTour} data-testid="dna-help-reiniciar-tour"><CirclePlay className="h-4 w-4" /> Refazer o tutorial guiado</Button>
        </SheetHeader>
        <div className="p-5">
          {!data ? <p className="text-sm" style={{ color: C.muted }}>Carregando…</p> : (
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="mb-3 h-auto flex-wrap gap-1 rounded-2xl border bg-white p-1" style={{ borderColor: C.line }}>
                {[['tutoriais', 'Tutoriais'], ['campos', 'Campos'], ['materiais', 'Manual e vídeo'], ...(rec?.can_configure ? [['config', 'Disponibilização']] : [])].map(([k, l]) => (
                  <TabsTrigger key={k} value={k} className="rounded-xl px-3 py-1.5 data-[state=active]:bg-[#2A1240] data-[state=active]:text-white" data-testid={`dna-help-tab-${k}`}>{k === 'config' && <Settings2 className="mr-1 h-3.5 w-3.5" />}{l}</TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="tutoriais"><Secoes secoes={data.secoes} editavel={data.editavel} section={section !== 'campos' ? section : null} /></TabsContent>
              <TabsContent value="campos"><Campos campos={data.campos} editavel={data.editavel} /></TabsContent>
              <TabsContent value="materiais"><Materiais materiais={data.materiais} editavel={data.editavel} /></TabsContent>
              {rec?.can_configure && <TabsContent value="config"><FeaturePanel rec={rec} onChange={setRec} /></TabsContent>}
            </Tabs>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
